import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RegisterUserFormService } from '../../../services/register-user-form.service';
import { Interest } from '../../../interfaces/register-user.response';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormField } from '../../form/abstracts';
import { RegisterUserService } from '../../../services/register-user.service';
import { MatError } from '@angular/material/form-field';
import { TranslationService } from 'src/app/services/translation.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';

@Component({
  selector: 'app-interests-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: InterestsFieldComponent
    },
  ],
  templateUrl: './interests-field.component.html',
  styleUrls: ['./interests-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InterestsFieldComponent extends BaseFormField<Interest[]> {
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  public isNextButtonClicked: InputSignal<boolean> = input<boolean>();
  
  private _cdr = inject(ChangeDetectorRef);
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _translationService = inject(TranslationService);
  private _breakpointsService = inject(BreakpointsService);
  
  private _formControlName = computed(() => this.form().get(this.formControlName()));
  private _interestList = this._registerUserService.registerUser().categories;
  private _currentInterestIdList = this._registerUserFormService.interests;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _isMobile = this._breakpointsService.isMobile;
  
  // TODO: PARA CICERONEWEB-SSR NO HARÁ FALTA SETEARLO POR EFFECT SEGÚN 
  // TODO: EL IDIOMA YA QUE CUANDO SE CAMBIA DE IDIOMA RECARGA LA PÁGINA
  // TODO: VIENE POR URL
  protected interestListChips = signal<Interest[]>([]);
  
  setInterestListChips = effect(() => {
    this.interestListChips.set(this._interestList
      .map(interest => {
        return {
          id: interest.id,
          value: interest.id,
          label: interest.name[this._selectedLanguage()],
          iconName: interest.icon
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, { allowSignalWrites: true });
  
  markAllFieldsAsTouched = effect(() => {
    if (this._markAllFormFieldsAsTouched()) {
      const control = this._formControlName();
      
      if (!control) return;
      
      // En mobile: NO marcar automáticamente como touched para evitar errores iniciales
      // Solo se marcará como touched cuando el usuario presione "Continuar" sin seleccionar
      if (this._isMobile()) {
        return;
      }
      
      control?.markAsTouched();
      control?.markAsDirty();

      // Triggear change detection manually for OnPush
      this._cdr.detectChanges();
    }
  }, { allowSignalWrites: true });
    
  public handleChipSetInterestsChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: number[] }>;
    const selectedInterests = customEvent.detail.values;
    this._registerUserFormService.setInterests(selectedInterests);
    
    const control = this.form().get(this.formControlName());
    control?.setValue(selectedInterests);
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      closeAllCiSnackbar();
    }
  }
  
  public isSelectedInterest(id: number): boolean {
    return this._currentInterestIdList().some(interestId => interestId === id);
  }
  
  public isInvalidField(): boolean {
    const control = this._formControlName();
    if (!control) return false;
    
    const hasRequiredError = control.hasError('required');
    const hasMinArrayLengthError = control.hasError('minArrayLength');
    
    if (!hasRequiredError && !hasMinArrayLengthError) return false;
    
    // En mobile: solo mostrar error si el control específico ha sido tocado
    // Esto evita que errores de slides anteriores afecten a este campo
    if (this._isMobile()) {
      return (control.touched || control.dirty) && this.isNextButtonClicked();
    }
    
    // En desktop: comportamiento normal
    return control.touched || control.dirty;
  }
}
